import React, { useEffect, useMemo, useRef, useState } from "react";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { useHistory, useParams } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import Axios from "axios";
import { DEV } from "../../services/constants";
import { Tooltip } from "react-tippy";
import { toast } from "react-toastify";
import { handlePutRequest } from "../../services/PutTemplate";
import SofStop from "../../modals/SofStop";
import { handlePostRequest } from "../../services/PostTemplate";
import { Dialog } from "primereact/dialog";
import { useDebounce } from "../../custom hooks/Debounce";
import { foldInvoiceCharges } from "../../services/additionalInvoiceService";
import { isUnassignedPurchase } from "../../services/purchaseInvoiceService";
import PurchaseInvoiceDialog from "./PurchaseInvoiceDialog";
import styles from "./BillingReports.module.scss";

function BillingReports() {
    const role = localStorage.getItem("role");
    const signref = useRef();
    const [selectedRow, setselectedRow] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    // Everything the endpoint returned for this status. Purchase invoices with
    // no flight number yet live in their own "Purchases" view; once a linked
    // package gives them a shipmentNumber they show up in the normal groups.
    const [allBrands, setBrands] = useState([]);
    const [view, setView] = useState("standard");
    const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
    const purchaseView = view === "purchases";
    const brands = useMemo(() => allBrands.filter((item) => isUnassignedPurchase(item) === purchaseView), [allBrands, purchaseView]);
    const unassignedPurchaseCount = useMemo(() => allBrands.filter(isUnassignedPurchase).length, [allBrands]);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [paginatedGroups, setPaginatedGroups] = useState([]);
    const [skip, setSkip] = useState(0);
    const dispatch = useDispatch();
    const [showSoftStop, setShowSoftStop] = useState(false);
    const [loading, setLoading] = useState(false);
    const [globalSearch, setGlobalSearch] = useState("");
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [groupsPerPage] = useState(5); // Show 5 groups per page

    const breadItems = [{ label: "Home", url: "/" }, { label: "Invoices" }];

    const home = { icon: "pi pi-home", url: "/" };
    const history = useHistory();
    const { status } = useParams();

    const [advancedFilters, setAdvancedFilters] = useState({
        invoiceId: "",
        invoiceNo: "",
        clientName: "",
        customerType: "",
        weight: "",
        invoiceAmount: "",
        shipmentNumber: "",
        descriptions: "",
        invoiceType: "",
        date: "",
        datePaid: "",
        paidAmount: "",
    });

    // Check if filters are active
    const isFilterActive = () => {
        return globalSearch !== "" || Object.values(advancedFilters).some((v) => v !== "");
    };

    // Group data by flight number for pending/unpaid
    const groupByFlightNumber = (data) => {
        const groups = {};
        data.forEach((item) => {
            const flightNumber = item.shipmentNumber || "No Flight Number";
            if (!groups[flightNumber]) {
                groups[flightNumber] = [];
            }
            groups[flightNumber].push(item);
        });
        return groups;
    };

    // Group data by payment date for paid invoices
    const groupByPaymentDate = (data) => {
        const groups = {};
        data.forEach((item) => {
            const paymentDate = item.datePaid || "No Payment Date";
            if (!groups[paymentDate]) {
                groups[paymentDate] = [];
            }
            groups[paymentDate].push(item);
        });
        // Sort groups by date (newest first)
        return Object.keys(groups)
            .sort((a, b) => new Date(b) - new Date(a))
            .reduce((acc, key) => {
                acc[key] = groups[key];
                return acc;
            }, {});
    };

    // Paginate groups
    useEffect(() => {
        const groupEntries = Object.entries(groupedData);

        // If filters are active, show all groups (no pagination)
        if (isFilterActive()) {
            setPaginatedGroups(groupEntries);
        } else {
            const start = currentPage * groupsPerPage;
            const end = start + groupsPerPage;
            setPaginatedGroups(groupEntries.slice(start, end));
        }
    }, [groupedData, currentPage, groupsPerPage, globalSearch, advancedFilters]);

    // Universal search function
    const performGlobalSearch = (data, searchTerm) => {
        if (!searchTerm || searchTerm.trim() === "") {
            return data;
        }

        const term = searchTerm.toLowerCase();
        return data.filter((item) => {
            return (
                item.invoiceId?.toString().toLowerCase().includes(term) ||
                item.invoiceNo?.toString().toLowerCase().includes(term) ||
                item.clientName?.toLowerCase().includes(term) ||
                item.customerType?.toLowerCase().includes(term) ||
                item.shipmentNumber?.toLowerCase().includes(term) ||
                item.descriptions?.toLowerCase().includes(term) ||
                item.invoiceType?.toLowerCase().includes(term) ||
                item.date?.toLowerCase().includes(term) ||
                item.datePaid?.toLowerCase().includes(term) ||
                item.weight?.toString().includes(term) ||
                item.invoiceAmount?.toString().includes(term) ||
                item.paidAmount?.toString().includes(term)
            );
        });
    };

    // Advanced filter function
    const performAdvancedFilter = (data) => {
        return data.filter((item) => {
            return (
                (!advancedFilters.invoiceId || item.invoiceId?.toString().toLowerCase().includes(advancedFilters.invoiceId.toLowerCase())) &&
                (!advancedFilters.invoiceNo || item.invoiceNo?.toString().toLowerCase().includes(advancedFilters.invoiceNo.toLowerCase())) &&
                (!advancedFilters.clientName || item.clientName?.toLowerCase().includes(advancedFilters.clientName.toLowerCase())) &&
                (!advancedFilters.customerType || item.customerType?.toLowerCase().includes(advancedFilters.customerType.toLowerCase())) &&
                (!advancedFilters.weight || item.weight?.toString().includes(advancedFilters.weight)) &&
                (!advancedFilters.invoiceAmount || item.invoiceAmount?.toString().includes(advancedFilters.invoiceAmount)) &&
                (!advancedFilters.shipmentNumber || item.shipmentNumber?.toLowerCase().includes(advancedFilters.shipmentNumber.toLowerCase())) &&
                (!advancedFilters.descriptions || item.descriptions?.toLowerCase().includes(advancedFilters.descriptions.toLowerCase())) &&
                (!advancedFilters.invoiceType || item.invoiceType?.toLowerCase().includes(advancedFilters.invoiceType.toLowerCase())) &&
                (!advancedFilters.date || item.date?.toLowerCase().includes(advancedFilters.date.toLowerCase())) &&
                (!advancedFilters.datePaid || item.datePaid?.toLowerCase().includes(advancedFilters.datePaid.toLowerCase())) &&
                (!advancedFilters.paidAmount || item.paidAmount?.toString().includes(advancedFilters.paidAmount))
            );
        });
    };

    // Apply all filters
    const applyFilters = (data) => {
        let filtered = data;

        // Apply global search
        filtered = performGlobalSearch(filtered, globalSearch);

        // Apply advanced filters
        filtered = performAdvancedFilter(filtered);

        return filtered;
    };

    useEffect(() => {
        const filtered = applyFilters(brands);
        setFilteredBrands(filtered);

        if (status === "paid") {
            setGroupedData(groupByPaymentDate(filtered));
        } else {
            setGroupedData(groupByFlightNumber(filtered));
        }

        // Reset to first page when filters change
        setCurrentPage(0);
    }, [globalSearch, advancedFilters, brands]);

    const getBrands = async () => {
        setLoading(true);
        try {
            if (status === "pending") {
                const result = await Axios.get(DEV + "/searchInvoice", {
                    params: {
                        reviewStatus: "pending",
                        paymentStatus: "pending",
                    },
                });
                const sortedData = result?.data?.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setBrands(sortedData);
                setFilteredBrands(sortedData);
                setGroupedData(groupByFlightNumber(sortedData));
            } else if (status === "unpaid") {
                const result = await Axios.get(DEV + "/searchInvoice", {
                    params: {
                        reviewStatus: "reviewed",
                        paymentStatus: "pending",
                        partial: "false",
                    },
                });
                const sortedData = result?.data?.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setBrands(sortedData);
                setFilteredBrands(sortedData);
                setGroupedData(groupByFlightNumber(sortedData));
            } else if (status === "partial") {
                // Deliberately not filtering by reviewStatus - same reasoning
                // as "paid" below: an invoice with real money already applied
                // to it belongs here regardless of the internal review flag.
                const result = await Axios.get(DEV + "/searchInvoice", {
                    params: {
                        paymentStatus: "pending",
                        partial: "true",
                    },
                });
                const sortedData = result?.data?.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setBrands(sortedData);
                setFilteredBrands(sortedData);
                setGroupedData(groupByFlightNumber(sortedData));
            } else if (status === "paid") {
                // Deliberately not filtering by reviewStatus here - a paid
                // invoice belongs in "Paid" regardless of whether it's been
                // reviewed yet, otherwise paid-but-unreviewed invoices never
                // show up in any tab (not here, not in "pending" or "unpaid"
                // since those require paymentStatus to be pending).
                const result = await Axios.get(DEV + "/searchPaidInvoice", {
                    params: {
                        paymentStatus: "paid",
                    },
                });
                setBrands(result?.data?.data);
                setFilteredBrands(result?.data?.data);
                setGroupedData(groupByPaymentDate(result?.data?.data));
            }
        } catch (error) {
            toast.error("Failed to fetch invoices");
            console.error(error);
        }
        setLoading(false);
    };

    useMemo(() => {
        getBrands();
        // Reset filters when status changes
        setGlobalSearch("");
        setAdvancedFilters({
            invoiceId: "",
            invoiceNo: "",
            clientName: "",
            customerType: "",
            weight: "",
            invoiceAmount: "",
            shipmentNumber: "",
            descriptions: "",
            invoiceType: "",
            date: "",
            datePaid: "",
            paidAmount: "",
        });
        setShowAdvancedSearch(false);
        setCurrentPage(0);
        setselectedRow([]);
        setView("standard");
    }, [status]);

    const handleActionButton = (rowdata) => {
        history.push(`/invoice?id=${rowdata._id}`);
    };

    const handleMarkedReviewed = async () => {
        if (selectedRow.length === 0) {
            toast.error("Please select atleast one invoice to mark as reviewed");
        } else {
            const ids = selectedRow.map((item) => item._id);
            const data = {
                id: ids,
                reviewStatus: "reviewed",
            };
            const res = await handlePutRequest(data, "/changeReviewStatusBulk");
            if (res?.success) {
                toast.success("Invoice marked as reviewed successfully");
                getBrands();
                setselectedRow([]);
            }
        }
    };

    const handleBulkInvoices = async () => {
        if (selectedRow.length === 0) {
            toast.error("Please select atleast one invoice to download");
        } else {
            const ids = selectedRow;
            localStorage.setItem("bulkInvoices", JSON.stringify(ids));
            history.push("/bulkInvoices");
        }
    };

    const onHideSoftStop = () => {
        setShowSoftStop(false);
    };

    const onSuccessSoftStop = async () => {
        setShowSoftStop(false);
        if (selectedRow?.length > 0) {
            const selectedIds = selectedRow.map((val) => {
                return val?._id;
            });
            const data = {
                id: selectedIds,
            };
            const res = await dispatch(handlePostRequest(data, "/deleteInvoices", true, true));
            if (res?.success) {
                getBrands();
                setselectedRow([]);
                toast.success("Invoice deleted");
            }
        } else {
            toast.error("Please select atleast one invoice to delete");
        }
    };

    const handledDelete = async () => {
        setShowSoftStop(true);
    };

    function capitalizeFirstWord(str) {
        str = str.toLowerCase();
        let words = str.split(" ");
        if (words.length > 0) {
            words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
        }
        return words.join(" ");
    }

    const handleReturnButtons = () => {
        const buttonConfig = {
            pending: [
                { label: "Unpaid Invoices", color: "blue", path: "/invoice/unpaid" },
                { label: "Partially Paid", color: "teal", path: "/invoice/partial" },
                { label: "Paid Invoices", color: "green", path: "/invoice/paid" },
            ],
            unpaid: [
                { label: "Pending Invoices", color: "orange", path: "/invoice/pending" },
                { label: "Partially Paid", color: "teal", path: "/invoice/partial" },
                { label: "Paid Invoices", color: "green", path: "/invoice/paid" },
            ],
            partial: [
                { label: "Pending Invoices", color: "orange", path: "/invoice/pending" },
                { label: "Unpaid Invoices", color: "blue", path: "/invoice/unpaid" },
                { label: "Paid Invoices", color: "green", path: "/invoice/paid" },
            ],
            paid: [
                { label: "Pending Invoices", color: "orange", path: "/invoice/pending" },
                { label: "Unpaid Invoices", color: "blue", path: "/invoice/unpaid" },
                { label: "Partially Paid", color: "teal", path: "/invoice/partial" },
            ],
        };

        return (
            <div className={styles.navigationButtons}>
                {buttonConfig[status]?.map((btn, idx) => (
                    <button key={idx} className={`${styles.navButton} ${styles[btn.color]}`} onClick={() => history.push(btn.path)}>
                        {btn.label}
                    </button>
                ))}
            </div>
        );
    };

    const handleResetInvoice = async () => {
        const data = selectedRow.map((item) => {
            return {
                _id: item._id,
                paidAmount: item?.paidAmount,
            };
        });
        const res = await handlePutRequest(data, "/resetInvoice");
        if (res?.success) {
            toast.success("Invoice reset successfully");
            getBrands();
            setselectedRow([]);
        }
    };

    const handleClearFilters = () => {
        setGlobalSearch("");
        setAdvancedFilters({
            invoiceId: "",
            invoiceNo: "",
            clientName: "",
            customerType: "",
            weight: "",
            invoiceAmount: "",
            shipmentNumber: "",
            descriptions: "",
            invoiceType: "",
            date: "",
            datePaid: "",
            paidAmount: "",
        });
        toast.info("Filters cleared");
    };

    const handleSelected = () => {
        if (!selectedRow?.length) {
            return toast.error("Please select at least one invoice to pay");
        }

        const hasPaidInvoice = selectedRow.some((item) => item.paymentStatus === "paid");
        if (hasPaidInvoice) {
            return toast.error("You can't select paid invoices");
        }

        if (selectedRow.length === 1) {
            return history.push(`/invoice?id=${selectedRow[0]._id}`);
        }

        const aggregated = selectedRow.reduce(
            (acc, item) => {
                foldInvoiceCharges(acc, item);
                acc.TotalAmount += item.TotalAmount;
                acc.discountedAmount += item.discountedAmount;
                acc.loyaltyDiscount += item.loyaltyDiscount;
                acc.splitFee += item.splitFee;
                acc.weight += item.weight;
                acc.descriptions += acc.descriptions ? `,${item.descriptions}` : item.descriptions;
                acc.shipmentNumber += acc.shipmentNumber ? `,${item.shipmentNumber}` : item.shipmentNumber;
                acc.Waybills += acc.Waybills ? `,${item.Waybills}` : item.Waybills;
                acc.invoiceAmount += item.invoiceAmount;
                acc.paidAmount += item.paidAmount ?? 0;
                acc.itemsPrice += item.invoiceCategory === "purchase" ? 0 : (item.itemsPrice ?? 0);
                acc.clientId = item.clientId;
                acc.clientName = item.clientName;
                acc.email = item.email;
                acc.phone = item.phone;
                acc.customerType = item.customerType;
                acc.date = item.date;
                acc.handling = item.handling ?? 0;
                acc.insurance = item.insurance ?? 0;
                acc.notes = acc.notes || "";
                return acc;
            },
            {
                MonthlyFee: 0,
                TotalAmount: 0,
                WebsiteFee: 0,
                dangerousItemFee: 0,
                deliveryFee: 0,
                customDuties: 0,
                declarationFee: 0,
                discountedAmount: 0,
                loyaltyDiscount: 0,
                oversizeFee: 0,
                packagingFee: 0,
                pickupFee: 0,
                splitFee: 0,
                weight: 0,
                packages: [],
                additionalCharges: [],
                purchaseItems: [],
                descriptions: "",
                shipmentNumber: "",
                Waybills: "",
                invoiceAmount: 0,
                paidAmount: 0,
                itemsPrice: 0,
                clientId: "",
                clientName: "",
                email: "",
                phone: "",
                customerType: "",
                date: "",
                handling: 0,
                insurance: 0,
                notes: "",
            },
        );

        const paidInvoices = selectedRow.map((item) => ({
            _id: item._id,
            paidAmount: item.invoiceAmount,
        }));

        localStorage.setItem("Zazioaggregated", JSON.stringify(aggregated));
        localStorage.setItem("Zaziopaid", JSON.stringify(paidInvoices));
        const paymentUser = role === "admin" || role === "clerk" ? "admin" : "user";
        localStorage.setItem("PaymentUser", paymentUser);
        history.push("/bulkInvoicesUser");
    };

    const isInvoiceSelected = (invoice) => {
        return selectedRow.some((item) => item._id === invoice._id);
    };

    const toggleInvoiceSelection = (invoice, e) => {
        e.stopPropagation();
        if (isInvoiceSelected(invoice)) {
            setselectedRow(selectedRow.filter((item) => item._id !== invoice._id));
        } else {
            setselectedRow([...selectedRow, invoice]);
        }
    };

    // Select/Deselect all filtered invoices
    const handleSelectAll = () => {
        if (selectedRow.length === filteredBrands.length && filteredBrands.length > 0) {
            // Deselect all
            setselectedRow([]);
        } else {
            // Select all filtered invoices
            setselectedRow([...filteredBrands]);
        }
    };

    const isAllSelected = () => {
        return filteredBrands.length > 0 && selectedRow.length === filteredBrands.length;
    };

    const isSomeSelected = () => {
        return selectedRow.length > 0 && selectedRow.length < filteredBrands.length;
    };

    const renderInvoiceCard = (invoice) => {
        const isSelected = isInvoiceSelected(invoice);

        return (
            <div key={invoice._id} className={`${styles.invoiceCard} ${isSelected ? styles.selected : ""} ${invoice.supersededBy ? styles.supersededCard : ""}`}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardCheckbox} onClick={(e) => toggleInvoiceSelection(invoice, e)}>
                        <input type="checkbox" checked={isSelected} onChange={(e) => toggleInvoiceSelection(invoice, e)} onClick={(e) => e.stopPropagation()} />
                        <span className={styles.invoiceId}>#{invoice.invoiceId}</span>
                        {invoice.supersededBy && <span className={styles.creditedBackBadge}>Credited Back</span>}
                    </div>
                    <button
                        className={styles.cardAction}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleActionButton(invoice);
                        }}
                    >
                        <i className="pi pi-eye" />
                    </button>
                </div>

                <div className={styles.cardBody}>
                    <div className={styles.cardRow}>
                        <div className={styles.cardField}>
                            <i className="pi pi-user" />
                            <div>
                                <span className={styles.fieldLabel}>Client</span>
                                <span className={styles.fieldValue}>{invoice.clientName}</span>
                            </div>
                        </div>
                        <div className={styles.cardField}>
                            <i className="pi pi-tag" />
                            <div>
                                <span className={styles.fieldLabel}>Type</span>
                                <span className={styles.fieldValue}>{invoice.customerType}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.cardRow}>
                        <div className={styles.cardField}>
                            <i className="pi pi-send" />
                            <div>
                                <span className={styles.fieldLabel}>Shipment</span>
                                <span className={styles.fieldValue}>{invoice.shipmentNumber || (invoice.invoiceCategory === "purchase" ? "Item Purchase" : "N/A")}</span>
                            </div>
                        </div>
                        <div className={styles.cardField}>
                            <i className="pi pi-box" />
                            <div>
                                <span className={styles.fieldLabel}>Weight</span>
                                <span className={styles.fieldValue}>{invoice.weight?.toFixed(2)} lb</span>
                            </div>
                        </div>
                    </div>

                    {invoice.descriptions && (
                        <div className={styles.cardDescription}>
                            <i className="pi pi-info-circle" />
                            <span>{invoice.descriptions}</span>
                        </div>
                    )}

                    <div className={styles.cardAmounts}>
                        <div className={styles.amountItem}>
                            <span className={styles.amountLabel}>Invoice Amount</span>
                            <span className={styles.amountValue}>${invoice.invoiceAmount?.toFixed(2)}</span>
                        </div>
                        {invoice.paidAmount > 0 && (
                            <div className={styles.amountItem}>
                                <span className={styles.amountLabel}>Paid</span>
                                <span className={`${styles.amountValue} ${styles.paid}`}>${invoice.paidAmount?.toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.cardFooter}>
                        <div className={styles.footerItem}>
                            <i className="pi pi-calendar" />
                            <span>{invoice.date}</span>
                        </div>
                        {status === "paid" && invoice.datePaid && (
                            <div className={styles.footerItem}>
                                <i className="pi pi-check-circle" />
                                <span>Paid: {invoice.datePaid}</span>
                            </div>
                        )}
                        {invoice.invoiceType && <div className={`${styles.invoiceTypeBadge} ${styles[invoice.invoiceType?.toLowerCase()]}`}>{invoice.invoiceType}</div>}
                    </div>
                </div>
            </div>
        );
    };

    const switchView = (next) => {
        setView(next);
        setselectedRow([]);
        setCurrentPage(0);
    };

    // Purchase invoices with no flight number yet live in their own tab.
    const renderViewTabs = () => (
        <div className={styles.viewTabs} role="tablist">
            <button type="button" role="tab" aria-selected={!purchaseView} className={`${styles.viewTab} ${!purchaseView ? styles.viewTabActive : ""}`} onClick={() => switchView("standard")}>
                <i className="pi pi-file" />
                Invoices
            </button>
            <button type="button" role="tab" aria-selected={purchaseView} className={`${styles.viewTab} ${purchaseView ? styles.viewTabActive : ""}`} onClick={() => switchView("purchases")}>
                <i className="pi pi-shopping-cart" />
                Purchases
                <span className={styles.viewTabCount}>{unassignedPurchaseCount}</span>
            </button>
        </div>
    );

    const renderSearchBar = () => {
        return (
            <div className={styles.searchContainer}>
                <div className={styles.globalSearchBar}>
                    <i className="pi pi-search" />
                    <input type="text" placeholder={`Search ${status} invoices by Invoice ID, Client Name, Shipment Number, Contents, etc...`} value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} className={styles.globalSearchInput} />
                    {globalSearch && (
                        <button className={styles.clearSearchBtn} onClick={() => setGlobalSearch("")}>
                            <i className="pi pi-times" />
                        </button>
                    )}
                </div>

                <div className={styles.searchActions}>
                    <button className={styles.advancedSearchToggle} onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}>
                        <i className={`pi ${showAdvancedSearch ? "pi-filter-slash" : "pi-filter"}`} />
                        {showAdvancedSearch ? "Hide Filters" : "Advanced Filters"}
                    </button>

                    {(globalSearch || Object.values(advancedFilters).some((v) => v)) && (
                        <button className={styles.clearAllBtn} onClick={handleClearFilters}>
                            <i className="pi pi-times-circle" />
                            Clear All
                        </button>
                    )}

                    <div className={styles.resultCount}>
                        <i className="pi pi-info-circle" />
                        <span>
                            Showing {filteredBrands.length} of {brands.length} invoices
                        </span>
                    </div>
                </div>

                {/* Select All Checkbox - Only show when filters are active */}
                {isFilterActive() && filteredBrands.length > 0 && (
                    <div className={styles.selectAllContainer}>
                        <div className={styles.selectAllCheckbox} onClick={handleSelectAll}>
                            <input
                                type="checkbox"
                                checked={isAllSelected()}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate = isSomeSelected();
                                    }
                                }}
                                onChange={handleSelectAll}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span>{isAllSelected() ? `Deselect all ${filteredBrands.length} filtered invoices` : isSomeSelected() ? `${selectedRow.length} selected - Select all ${filteredBrands.length} filtered invoices` : `Select all ${filteredBrands.length} filtered invoices`}</span>
                        </div>
                    </div>
                )}

                {showAdvancedSearch && (
                    <div className={styles.advancedFilters}>
                        <h4>
                            <i className="pi pi-sliders-h" />
                            Advanced Search Filters
                        </h4>
                        <div className={styles.filterGrid}>
                            <div className={styles.filterField}>
                                <label>Invoice ID</label>
                                <input type="text" placeholder="Search by Invoice ID..." value={advancedFilters.invoiceId} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceId: e.target.value })} />
                            </div>

                            {status === "paid" && (
                                <div className={styles.filterField}>
                                    <label>Payment ID</label>
                                    <input type="text" placeholder="Search by Payment ID..." value={advancedFilters.invoiceNo} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceNo: e.target.value })} />
                                </div>
                            )}

                            <div className={styles.filterField}>
                                <label>Client Name</label>
                                <input type="text" placeholder="Search by Client Name..." value={advancedFilters.clientName} onChange={(e) => setAdvancedFilters({ ...advancedFilters, clientName: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Customer Type</label>
                                <input type="text" placeholder="Search by Customer Type..." value={advancedFilters.customerType} onChange={(e) => setAdvancedFilters({ ...advancedFilters, customerType: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Shipment Number</label>
                                <input type="text" placeholder="Search by Shipment Number..." value={advancedFilters.shipmentNumber} onChange={(e) => setAdvancedFilters({ ...advancedFilters, shipmentNumber: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Contents/Description</label>
                                <input type="text" placeholder="Search by Contents..." value={advancedFilters.descriptions} onChange={(e) => setAdvancedFilters({ ...advancedFilters, descriptions: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Invoice Type</label>
                                <input type="text" placeholder="Search by Invoice Type..." value={advancedFilters.invoiceType} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceType: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Issue Date</label>
                                <input type="text" placeholder="Search by Issue Date..." value={advancedFilters.date} onChange={(e) => setAdvancedFilters({ ...advancedFilters, date: e.target.value })} />
                            </div>

                            {status === "paid" && (
                                <div className={styles.filterField}>
                                    <label>Payment Date</label>
                                    <input type="text" placeholder="Search by Payment Date..." value={advancedFilters.datePaid} onChange={(e) => setAdvancedFilters({ ...advancedFilters, datePaid: e.target.value })} />
                                </div>
                            )}

                            <div className={styles.filterField}>
                                <label>Weight</label>
                                <input type="text" placeholder="Search by Weight..." value={advancedFilters.weight} onChange={(e) => setAdvancedFilters({ ...advancedFilters, weight: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Invoice Amount</label>
                                <input type="text" placeholder="Search by Amount..." value={advancedFilters.invoiceAmount} onChange={(e) => setAdvancedFilters({ ...advancedFilters, invoiceAmount: e.target.value })} />
                            </div>

                            <div className={styles.filterField}>
                                <label>Paid Amount</label>
                                <input type="text" placeholder="Search by Paid Amount..." value={advancedFilters.paidAmount} onChange={(e) => setAdvancedFilters({ ...advancedFilters, paidAmount: e.target.value })} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const onPageChange = (event) => {
        setCurrentPage(event.page);
    };

    const renderGroupedInvoices = () => {
        if (paginatedGroups.length === 0) {
            return null;
        }

        if (status === "paid") {
            return paginatedGroups.map(([date, invoices]) => (
                <div key={date} className={styles.groupContainer}>
                    <div className={styles.dateBanner}>
                        <i className="pi pi-calendar" />
                        <span>Payment Date: {date}</span>
                        <span className={styles.count}>({invoices.length} invoices)</span>
                    </div>
                    <div className={styles.cardsGrid}>{invoices.map((invoice) => renderInvoiceCard(invoice))}</div>
                </div>
            ));
        } else {
            return paginatedGroups.map(([flightNumber, invoices]) => (
                <div key={flightNumber} className={styles.groupContainer}>
                    <div className={styles.flightBanner}>
                        <i className="pi pi-send" />
                        <span>Flight Number: {flightNumber}</span>
                        <span className={styles.count}>({invoices.length} invoices)</span>
                    </div>
                    <div className={styles.cardsGrid}>{invoices.map((invoice) => renderInvoiceCard(invoice))}</div>
                </div>
            ));
        }
    };

    const totalGroups = Object.keys(groupedData).length;
    const selectedTotal = selectedRow.reduce((sum, item) => sum + (item.invoiceAmount || 0), 0);

    return (
        <>
            <Dialog visible={showSoftStop} style={{ width: "650px" }} onHide={() => setShowSoftStop(false)}>
                <SofStop onHideSoftStop={onHideSoftStop} onSuccessSoftStop={onSuccessSoftStop} type="Invoice" />
            </Dialog>

            <Dialog visible={showPurchaseDialog} header="New Purchase Invoice" style={{ width: "720px", maxWidth: "95vw" }} contentStyle={{ overflowX: "hidden" }} onHide={() => setShowPurchaseDialog(false)}>
                <PurchaseInvoiceDialog
                    onHideDialog={() => setShowPurchaseDialog(false)}
                    onsuccess={(invoice) => {
                        setShowPurchaseDialog(false);
                        toast.success(`Purchase invoice ${invoice?.invoiceId || ""} created`);
                        getBrands();
                    }}
                />
            </Dialog>

            <div className={styles.pageContainer}>
                <div className={styles.pageHeader}>
                    <div className={styles.headerLeft}>
                        <h1 className={styles.pageTitle}>{status === "partial" ? "Partially Paid Invoices" : `${capitalizeFirstWord(status)} Invoices`}</h1>
                        <div className={styles.statsBar}>
                            <div className={styles.statItem}>
                                <i className="pi pi-file" />
                                <span>{brands.length} Total Invoices</span>
                            </div>
                            {selectedRow.length > 0 && (
                                <div className={styles.statItem}>
                                    <i className="pi pi-check-square" />
                                    <span>{selectedRow.length} Selected</span>
                                </div>
                            )}
                            <div className={styles.statItem}>
                                <i className="pi pi-box" />
                                <span>{totalGroups} Groups</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.actionBar}>
                        {role === "admin" && (
                            <Button icon="pi pi-plus" label="New Purchase Invoice" onClick={() => setShowPurchaseDialog(true)} className={styles.btnAction} />
                        )}
                        {status === "paid" && (
                            <Tooltip title="Reset selected invoices" position="bottom" trigger="mouseenter" arrow={true}>
                                <Button
                                    icon="pi pi-refresh"
                                    label="Reset"
                                    onClick={() => {
                                        if (selectedRow.length > 0) {
                                            handleResetInvoice();
                                        } else {
                                            toast.error("Please select atleast one invoice to reset");
                                        }
                                    }}
                                    className={styles.btnAction}
                                />
                            </Tooltip>
                        )}

                        {status !== "partial" && (
                            <Tooltip title="Generate Invoice Report" position="bottom" trigger="mouseenter" arrow={true}>
                                <Button
                                    icon="pi pi-plus"
                                    label="Generate"
                                    onClick={() => {
                                        if (status === "pending") {
                                            history.push("/generateInvoice");
                                        } else if (status === "unpaid") {
                                            history.push("/report/unpaid");
                                        } else if (status === "paid") {
                                            history.push("/report/paid");
                                        }
                                    }}
                                    className={styles.btnAction}
                                />
                            </Tooltip>
                        )}

                        {role === "admin" && (
                            <Tooltip title="Mark invoices as reviewed" position="bottom" trigger="mouseenter" arrow={true}>
                                <Button icon="pi pi-search-plus" label="Mark Reviewed" onClick={handleMarkedReviewed} className={styles.btnAction} />
                            </Tooltip>
                        )}

                        <Tooltip title="Download selected Invoices" position="bottom" trigger="mouseenter" arrow={true}>
                            <Button icon="pi pi-file-pdf" label="Download" onClick={handleBulkInvoices} className={styles.btnAction} />
                        </Tooltip>

                        {role === "admin" && (
                            <Tooltip title="Delete selected Invoices" position="bottom" trigger="mouseenter" arrow={true}>
                                <Button icon="pi pi-trash" label="Delete" onClick={handledDelete} className={styles.btnDanger} />
                            </Tooltip>
                        )}

                        <Tooltip title={selectedRow.length ? "Pay Selected Invoices" : "Select invoices to pay"} position="bottom" trigger="mouseenter" arrow={true}>
                            <Button icon="pi pi-wallet" label={selectedRow.length > 0 ? `Pay $${selectedTotal.toFixed(2)}` : "Pay"} onClick={handleSelected} className={styles.btnPrimary} disabled={selectedRow.length === 0} />
                        </Tooltip>
                    </div>
                </div>

                {handleReturnButtons()}

                {renderViewTabs()}

                {renderSearchBar()}

                <div className={styles.contentArea}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <i className="pi pi-spinner pi-spin" style={{ fontSize: "3rem" }} />
                            <p>Loading invoices...</p>
                        </div>
                    ) : filteredBrands.length === 0 ? (
                        <div className={styles.emptyState}>
                            <i className="pi pi-inbox" style={{ fontSize: "4rem" }} />
                            <h3>No invoices found</h3>
                            <p>{globalSearch || Object.values(advancedFilters).some((v) => v) ? "No invoices match your search criteria. Try adjusting your filters." : `There are no ${status} invoices at the moment.`}</p>
                            {(globalSearch || Object.values(advancedFilters).some((v) => v)) && (
                                <button className={styles.clearFiltersBtn} onClick={handleClearFilters}>
                                    <i className="pi pi-filter-slash" />
                                    Clear All Filters
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {renderGroupedInvoices()}

                            {/* Only show pagination when filters are NOT active */}
                            {!isFilterActive() && totalGroups > groupsPerPage && (
                                <div className={styles.paginationContainer}>
                                    <Paginator
                                        first={currentPage * groupsPerPage}
                                        rows={groupsPerPage}
                                        totalRecords={totalGroups}
                                        onPageChange={onPageChange}
                                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} groups"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default BillingReports;
